import { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const STRIP = ["_eff_glass"];

export const RefineBox = ({ item, update, projectId, hasPdf }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const latest = useRef(item);
  latest.current = item;
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const can = !!item.source || hasPdf;

  const run = async (withNote) => {
    setBusy(true);
    const base = item;
    try {
      const body = { ...item };
      STRIP.forEach((k) => delete body[k]);
      const { data } = await api.post(`/projects/${projectId}/items/${item.id}/refine`, { item: body, note: withNote ? note : "" });
      const t0 = Date.now();
      timer.current = setInterval(async () => {
        try {
          const { data: r } = await api.get(`/refines/${data.refine_id}`);
          if (r.status === "processing") {
            if (Date.now() - t0 < 240000) return;
            clearInterval(timer.current); setBusy(false);
            return toast.error("AI is taking too long — please try again");
          }
          clearInterval(timer.current);
          setBusy(false);
          if (r.status === "ready") {
            update({ ...(latest.current.id === base.id ? latest.current : base), ...r.result });
            setNote("");
            toast.success(`${item.tag} re-matched to the drawing — press Undo to revert`);
          } else toast.error(r.error || "AI could not correct this item");
        } catch (e) { clearInterval(timer.current); setBusy(false); toast.error(errMsg(e)); }
      }, 3000);
    } catch (e) { setBusy(false); toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-md border border-slate-700 p-3 space-y-2 bg-[#0B0F19]/60" data-testid="refine-box">
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Doesn't match the joinery drawing?</p>
      <textarea data-testid="refine-note-input" value={note} onChange={(e) => setNote(e.target.value)} disabled={!can || busy} rows={2}
        placeholder="e.g. left panel should be a fixed side light 450 wide; fanlight is 2 panels"
        className="w-full rounded-md bg-[#0B0F19] border border-slate-700 px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:border-cyan-500 disabled:opacity-50" />
      <div className="grid grid-cols-2 gap-2">
        <button data-testid="refine-recheck-btn" onClick={() => run(false)} disabled={!can || busy}
          className="h-8 rounded-md border border-slate-700 text-[11px] text-slate-200 flex items-center justify-center gap-1.5 hover:border-cyan-500 hover:text-cyan-300 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <ScanSearch size={12} />}Re-check drawing
        </button>
        <button data-testid="refine-fix-btn" onClick={() => run(true)} disabled={!can || busy || !note.trim()}
          className="h-8 rounded-md bg-cyan-500 text-slate-950 text-[11px] font-semibold flex items-center justify-center gap-1.5 hover:bg-cyan-400 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}Fix with AI
        </button>
      </div>
      <p className="text-[10px] text-slate-500">{can ? (item.source ? "AI compares this item against its cropped source elevation (use Compare in the toolbar)." : "AI re-reads this tag from the source PDF.") : "No source drawing for this item."}</p>
    </div>
  );
};
